// The player's Bluesky session (from app-password login). Content is pulled
// through THIS session — their own account, their own rate budget — via the
// worker xrpc proxy. Persisted so a refresh keeps you connected; auto-refreshes
// the access token on expiry.
import { create } from 'zustand'
import { apiSend } from '../api/client'
import type { BskySession } from '../types'

const KEY = 'gg_bsky'

function persist(s: BskySession | null) {
  try {
    if (s) localStorage.setItem(KEY, JSON.stringify(s))
    else localStorage.removeItem(KEY)
  } catch {
    /* ignore */
  }
}

interface BskyState {
  session: BskySession | null
  ready: boolean
  load: () => void
  setSession: (s: BskySession) => void
  clear: () => void
  /** Refresh the access token (shared in-flight). */
  refresh: () => Promise<BskySession>
}

let refreshing: Promise<BskySession> | null = null

export const useBsky = create<BskyState>((set, get) => ({
  session: null,
  ready: false,
  load() {
    try {
      const raw = localStorage.getItem(KEY)
      set({ session: raw ? (JSON.parse(raw) as BskySession) : null, ready: true })
    } catch {
      set({ ready: true })
    }
  },
  setSession(s) {
    persist(s)
    set({ session: s })
  },
  clear() {
    persist(null)
    set({ session: null })
  },
  async refresh() {
    if (refreshing) return refreshing
    const s = get().session
    if (!s) throw new Error('not connected')
    refreshing = (async () => {
      try {
        const fresh = await apiSend<{ accessJwt: string; refreshJwt: string }>(
          '/api/auth/refresh',
          { body: { service: s.service, refreshJwt: s.refreshJwt } },
        )
        const next: BskySession = { ...s, accessJwt: fresh.accessJwt, refreshJwt: fresh.refreshJwt }
        get().setSession(next)
        return next
      } finally {
        refreshing = null
      }
    })()
    return refreshing
  },
}))
